import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiAffaireTasks } from '../api/client.js';

/**
 * Ticket #76 — sélecteur de tâche d'affaire, PARTAGÉE entre collaborateurs.
 *
 * Float portait cette notion : la tâche créée par un collaborateur apparaissait
 * aussitôt dans la liste de ses collègues sur la même affaire, et rien
 * n'empêchait d'en créer une nouvelle. La reprise l'avait fait atterrir dans un
 * champ texte libre où chacun retapait le libellé de mémoire — d'où des
 * doublons de graphie et la sensation que la fonction avait disparu.
 *
 * Le composant reproduit les deux gestes de Float en un seul champ : on tape,
 * la liste se filtre, on choisit ; si rien ne correspond, la première entrée du
 * menu propose de créer la tâche. Un champ `<select>` pur interdirait la
 * création, un champ libre seul ne montrerait pas le partage : c'est la
 * combinaison qui rend la fonction visible.
 *
 * Contrat avec le parent : `onChange({ taskId, taskName })`. `taskId` désigne
 * une tâche existante, `taskName` un libellé à créer côté serveur — jamais les
 * deux. Cette forme est celle qu'attendent les payloads d'allocation et de
 * saisie, ce qui évite au parent de traduire quoi que ce soit.
 */

/**
 * Requêtes EN VOL par affaire — et rien de plus.
 *
 * Ce n'est volontairement plus un cache de résultats. La première version
 * mémorisait la liste d'une affaire pour toute la durée de la page, sans
 * jamais la rafraîchir : une tâche créée à la volée en enregistrant une
 * allocation n'apparaissait donc pas quand on rouvrait un autre jour de la
 * même affaire, tant que la page n'était pas rechargée. Or c'est précisément
 * le geste que le ticket #76 demande de rétablir — créer une tâche un jour, la
 * resélectionner le lendemain.
 *
 * On ne garde que le dédoublonnage des appels simultanés (plusieurs pickers
 * montés en même temps sur la même affaire) ; la liste est relue à chaque
 * ouverture. Elle est bornée à 500 côté serveur, le coût est négligeable.
 */
const inflight = new Map();

/** Conservé pour les appelants existants : abandonne une requête en vol. */
export function invalidateTaskCache(affaireId) {
  if (affaireId) inflight.delete(affaireId);
  else inflight.clear();
}

/**
 * Charge les tâches d'une affaire. Exporté pour les vues qui ont besoin de la
 * liste sans afficher le sélecteur.
 */
export async function loadTasks(affaireId) {
  if (!affaireId) return [];
  const pending = inflight.get(affaireId);
  if (pending) return pending;
  const promise = apiAffaireTasks
    .list(affaireId)
    .then((data) => data?.items ?? [])
    .catch(() => [])
    // Le référentiel est un confort de saisie : son indisponibilité ne doit
    // pas empêcher d'enregistrer une allocation ou une imputation.
    .finally(() => inflight.delete(affaireId));
  inflight.set(affaireId, promise);
  return promise;
}

export default function TaskPicker({
  affaireId,
  value,
  onChange,
  disabled,
  inputId,
  style,
  placeholder = 'Rechercher ou créer une tâche…',
}) {
  const [tasks, setTasks] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadTasks(affaireId).then((items) => {
      if (!cancelled) setTasks(items);
    });
    return () => {
      cancelled = true;
    };
  }, [affaireId]);

  // La tâche sélectionnée peut ne pas figurer dans la liste : elle est archivée,
  // ou le référentiel n'a pas (encore) répondu. On affiche alors le libellé
  // transmis par le parent plutôt qu'un champ vide, qui laisserait croire que
  // le rattachement a été perdu.
  const selectedLabel = useMemo(() => {
    if (!value?.taskId) return value?.taskName ?? '';
    const found = tasks.find((t) => t.id === value.taskId);
    return found?.name ?? value.taskName ?? '';
  }, [tasks, value]);

  const trimmed = query.trim();
  const matches = useMemo(() => {
    if (!trimmed) return tasks;
    const q = trimmed.toLowerCase();
    return tasks.filter((t) => t.name.toLowerCase().includes(q));
  }, [tasks, trimmed]);

  const exact = useMemo(
    () => tasks.find((t) => t.name.toLowerCase() === trimmed.toLowerCase()),
    [tasks, trimmed],
  );
  const canCreate = trimmed.length > 0 && !exact;

  // Les entrées du menu, dans l'ordre affiché — une seule liste pour le rendu
  // ET pour la navigation clavier, sinon les deux finissent par diverger.
  const options = useMemo(() => {
    const rows = matches.map((t) => ({ kind: 'task', task: t }));
    if (canCreate) rows.unshift({ kind: 'create', name: trimmed });
    return rows;
  }, [matches, canCreate, trimmed]);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const commit = useCallback(
    (option) => {
      if (!option) return;
      if (option.kind === 'create') {
        onChange({ taskId: null, taskName: option.name });
      } else {
        onChange({ taskId: option.task.id, taskName: option.task.name });
      }
      setQuery('');
      setOpen(false);
    },
    [onChange],
  );

  const clear = useCallback(() => {
    onChange({ taskId: null, taskName: null });
    setQuery('');
  }, [onChange]);

  /**
   * Valide ce qui est TAPÉ quand on quitte le champ sans avoir choisi dans le
   * menu.
   *
   * Sans cela, taper « test » puis cliquer directement sur « Enregistrer »
   * fermait le menu et jetait la saisie en silence : le libellé était encore à
   * l'écran au moment du clic, mais l'allocation partait sans tâche. C'est
   * exactement ce qui a été constaté en recette. Ce qui est affiché dans le
   * champ est ce qui est enregistré : un libellé qui correspond à une tâche
   * existante (casse ignorée, comme côté serveur) la sélectionne, sinon il
   * devient une création.
   *
   * Un champ vidé ne détache rien : pour retirer une tâche il y a le bouton ✕,
   * et effacer par erreur un texte en cours de frappe ne doit pas faire perdre
   * une tâche déjà choisie.
   */
  const commitTyped = useCallback(() => {
    const typed = query.trim();
    if (!typed) {
      setOpen(false);
      return;
    }
    const existing = tasks.find((t) => t.name.toLowerCase() === typed.toLowerCase());
    commit(existing ? { kind: 'task', task: existing } : { kind: 'create', name: typed });
  }, [query, tasks, commit]);

  // Clic extérieur : on valide la saisie en cours, puis on ferme. `pointerdown`
  // précède le `click` du bouton « Enregistrer » et React applique la mise à
  // jour avant ce clic : le formulaire parent part donc avec la tâche.
  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) commitTyped();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open, commitTyped]);

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!open) {
        setOpen(true);
        return;
      }
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      setHighlight((h) => {
        if (options.length === 0) return 0;
        return (h + delta + options.length) % options.length;
      });
    } else if (e.key === 'Enter') {
      // Sans option sous le curseur, laisser l'Entrée remonter validerait la
      // modale entière alors que l'utilisateur croit valider sa tâche.
      if (open && options.length > 0) {
        e.preventDefault();
        commit(options[highlight]);
      }
    } else if (e.key === 'Escape') {
      if (open) {
        e.stopPropagation();
        // Échap ANNULE : on jette le texte, faute de quoi le blur qui suit
        // validerait précisément ce que l'utilisateur vient d'abandonner.
        setQuery('');
        setOpen(false);
      }
    } else if (e.key === 'Tab') {
      // Tabuler hors du champ vaut validation, comme un clic ailleurs.
      if (open) commitTyped();
    }
  };

  const hasSelection = Boolean(value?.taskId || value?.taskName);

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          autoComplete="off"
          disabled={disabled}
          value={open ? query : selectedLabel}
          placeholder={hasSelection ? selectedLabel : placeholder}
          onFocus={() => setOpen(true)}
          onBlur={() => {
            // Filet pour les pertes de focus qui ne passent ni par un clic
            // extérieur ni par Tab. Un clic sur une option ne déclenche pas de
            // blur (son `pointerdown` fait `preventDefault`).
            if (open) commitTyped();
          }}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          style={{ ...style, flex: 1 }}
        />
        {hasSelection && !disabled ? (
          <button
            type="button"
            onClick={clear}
            title="Retirer la tâche"
            style={{
              background: 'transparent',
              border: '1px solid var(--glass-border)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--text-muted)',
              cursor: 'pointer',
              padding: '4px 8px',
            }}
          >
            ✕
          </button>
        ) : null}
      </div>

      {open && !disabled ? (
        <div
          role="listbox"
          style={{
            position: 'absolute',
            zIndex: 10,
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            maxHeight: 220,
            overflowY: 'auto',
            background: 'var(--card-bg)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-sm)',
            boxShadow: 'var(--shadow-card)',
          }}
        >
          {options.length === 0 ? (
            <div style={{ padding: '8px 10px', fontSize: 13, color: 'var(--text-muted)' }}>
              Aucune tâche sur cette affaire
            </div>
          ) : (
            options.map((o, idx) => {
              const label = o.kind === 'create' ? `Créer « ${o.name} »` : o.task.name;
              const key = o.kind === 'create' ? '__create__' : o.task.id;
              return (
                <div
                  key={key}
                  role="option"
                  aria-selected={idx === highlight}
                  onPointerDown={(e) => {
                    // `pointerdown` et non `click` : le blur de l'input
                    // fermerait le menu avant que le clic n'aboutisse.
                    e.preventDefault();
                    commit(o);
                  }}
                  onPointerEnter={() => setHighlight(idx)}
                  style={{
                    padding: '7px 10px',
                    fontSize: 13,
                    cursor: 'pointer',
                    background: idx === highlight ? 'var(--glass)' : 'transparent',
                    color: o.kind === 'create' ? 'var(--accent)' : 'var(--text)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 8,
                  }}
                >
                  <span
                    style={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {label}
                  </span>
                  {o.kind === 'task' && o.task.usageCount > 0 ? (
                    <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {o.task.usageCount}
                    </span>
                  ) : null}
                </div>
              );
            })
          )}
        </div>
      ) : null}
    </div>
  );
}
